import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from '@symphenia/ui-components'
import { SITE_DOC_KINDS, SITE_DOC_KIND_MODULE, SITE_DOC_KIND_PERMISSION, erfuelltRecht, type SiteDocKind, type SiteDocProjectChoice, type SiteDocProjectSource } from '@symphenia/shared-types'
import { useAuthStore } from '@/lib/auth.store'
import { fehlertext } from '@/lib/fehlertext'
import { useTerminology } from '@/lib/useTerminology'
import { cn } from '@/lib/utils'
import { createSiteDoc, fetchProjectChoices } from '../api/site-docs.api'
import { erpArt, type ErpAnlegenWert } from '../arten/registry'
import '../arten'

const heute = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/**
 * AP-46 11.2, AP-36 Fall 2 — **Neues Baustellendokument im ERP**: nur, was zum Anlegen nötig ist —
 * Art, Projekt (aus dem Projektstamm, der Zeiterfassung oder frei eingetragen, 24.2), Datum, Titel.
 * Danach öffnet sich das Dokument.
 */
export function NeuesDokumentDialog({ onClose, onAngelegt }: { onClose: () => void; onAngelegt: (id: string) => void }) {
  const { t } = useTranslation()
  const terms = useTerminology()
  const { user, hasModule } = useAuthStore()
  const arten = SITE_DOC_KINDS.filter((k) => hasModule(SITE_DOC_KIND_MODULE[k]) && erfuelltRecht(SITE_DOC_KIND_PERMISSION[k], user?.permissions))
  const [kind, setKind] = useState<SiteDocKind | null>(arten.length === 1 ? arten[0] : null)
  const [quellen, setQuellen] = useState<SiteDocProjectSource[]>([])
  const [auswahl, setAuswahl] = useState<SiteDocProjectChoice[]>([])
  const [suche, setSuche] = useState('')
  const [projekt, setProjekt] = useState<SiteDocProjectChoice | null>(null)
  const [frei, setFrei] = useState(false)
  const [freiText, setFreiText] = useState({ number: '', name: '', customer: '', site: '' })
  const [datum, setDatum] = useState(heute())
  const [titel, setTitel] = useState('')
  const [fehler, setFehler] = useState<string | null>(null)
  const [arbeitet, setArbeitet] = useState(false)
  // AP-49: Felder der Art im Dialog (Besprechung: Besprechungsart; der Titel kommt dann vom Server).
  const ArtAnlegen = kind ? erpArt(kind)?.Anlegen : undefined
  const [zusatz, setZusatz] = useState<ErpAnlegenWert | null>(null)
  useEffect(() => setZusatz(null), [kind])
  const titelAutomatisch = !!ArtAnlegen && !!zusatz?.titelAutomatisch

  useEffect(() => {
    const zeit = setTimeout(() => {
      fetchProjectChoices(suche.trim() || undefined)
        .then((r) => {
          setAuswahl(r.data)
          setQuellen(r.sources)
        })
        .catch(() => setAuswahl([]))
    }, suche ? 300 : 0)
    return () => clearTimeout(zeit)
  }, [suche])

  const bereit = !!kind && (titelAutomatisch ? !!zusatz?.content : !!titel.trim()) && (frei ? !!(freiText.name.trim() || freiText.number.trim()) : !!projekt)

  async function anlegen() {
    if (!kind || !bereit) return
    setArbeitet(true)
    setFehler(null)
    try {
      const neu = await createSiteDoc(kind, {
        project: frei
          ? { source: 'TEXT', text: { number: freiText.number.trim() || null, name: freiText.name.trim() || null, customer: freiText.customer.trim() || null, site: freiText.site.trim() || null } }
          : { source: projekt!.source, projectId: projekt!.source === 'PROJECT' ? projekt!.id : null, projectRefId: projekt!.source === 'TIME_REF' ? projekt!.id : null },
        documentDate: datum,
        title: titel.trim() || t(`siteDocs.kinds.${kind}`),
        ...(ArtAnlegen && zusatz?.content ? { content: zusatz.content } : {}),
      })
      onAngelegt(neu.id)
    } catch (e) {
      setFehler(fehlertext(e, t))
      setArbeitet(false)
    }
  }

  const feld = 'h-10 w-full rounded-md border border-input bg-background px-3 text-sm'

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={t('siteDocs.create.title')}
      size="md"
      footer={
        <div className="flex w-full justify-end gap-2">
          <button type="button" onClick={onClose} className="h-10 rounded-md border px-4 text-sm">
            {t('common.cancel')}
          </button>
          <button type="button" disabled={!bereit || arbeitet} onClick={() => void anlegen()} className="h-10 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground disabled:opacity-50">
            {t('siteDocs.create.submit')}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        {fehler && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{fehler}</p>}
        {arten.length > 1 && (
          <div className="flex flex-wrap gap-2">
            {arten.map((k) => (
              <button key={k} type="button" onClick={() => setKind(k)} className={cn('h-9 rounded-md border px-3 text-sm', kind === k && 'border-primary bg-primary/10')}>
                {t(`siteDocs.kinds.${k}`)}
              </button>
            ))}
          </div>
        )}
        {ArtAnlegen && <ArtAnlegen onWert={setZusatz} />}
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{terms.project}</p>
          {!frei && (
            <>
              {quellen.some((q) => q !== 'TEXT') && <input className={feld} placeholder={t('siteDocs.create.search')} value={suche} onChange={(e) => setSuche(e.target.value)} />}
              <ul className="max-h-56 space-y-1 overflow-y-auto">
                {auswahl.map((p) => (
                  <li key={`${p.source}-${p.id}`}>
                    <button type="button" onClick={() => setProjekt(p)} className={cn('w-full rounded-md border px-3 py-1.5 text-left text-sm', projekt?.id === p.id && 'border-primary bg-primary/10')}>
                      <span className="font-medium">{[p.number, p.name].filter(Boolean).join(' ')}</span>
                      {p.customerName && <span className="ml-2 text-xs text-muted-foreground">{p.customerName}</span>}
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
          <button type="button" onClick={() => { setFrei((f) => !f); setProjekt(null) }} className="text-sm text-primary hover:underline">
            {frei ? t('siteDocs.create.fromList') : t('siteDocs.create.free')}
          </button>
          {frei && (
            <div className="grid gap-2 sm:grid-cols-2">
              {(['number', 'name', 'customer', 'site'] as const).map((k) => (
                <input key={k} className={feld} placeholder={t(`siteDocs.create.freeFields.${k}`)} value={freiText[k]} onChange={(e) => setFreiText((f) => ({ ...f, [k]: e.target.value }))} />
              ))}
            </div>
          )}
        </div>
        <div className="grid gap-2 sm:grid-cols-[10rem_1fr]">
          <label className="text-sm">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('siteDocs.preview.date')}</span>
            <input type="date" className={feld} value={datum} onChange={(e) => setDatum(e.target.value)} />
          </label>
          {!titelAutomatisch && (
            <label className="text-sm">
              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('siteDocs.columns.title')}</span>
              <input className={feld} value={titel} onChange={(e) => setTitel(e.target.value)} />
            </label>
          )}
        </div>
      </div>
    </Modal>
  )
}
